import {FieldError as RhfFieldError} from "react-hook-form";
import i18next from "i18next";
import {Alert, Box, Button, Input, InputProps, Stack} from "@mui/joy";
import React, {useCallback, useState} from "react";
import {faEye, faPencil, faSearch} from "@fortawesome/pro-regular-svg-icons";
import {useTranslation} from "react-i18next";
import {FontAwesomeIcon} from "@fortawesome/react-fontawesome";
import {z} from "zod/v4";

export const EditorMode = z.enum(["r", "rw"]);
export type EditorMode = z.infer<typeof EditorMode>;

export const editModeIcons = {
    r: faEye,
    rw: faPencil,
};

const editModeSearchKey = "em";

export function validateEditModeSearchParams(raw: Record<string, unknown>): { editMode?: EditorMode } {
    if ("rw" === raw[editModeSearchKey]) {
        return {editMode: "rw"};
    } else {
        return {editMode: "r"};
    }
}

export function ReactHookFormValidationErrorAlert({error}: { error: RhfFieldError | null | undefined }) {
    if (!error) {
        return null;
    }

    let msg: string;
    if (error.message) {
        msg = error.message;
    } else {
        switch (error.type) {
            case "required":
                msg = i18next.t("required");
                break;
            default:
                msg = JSON.stringify(error);
        }
    }

    return <Alert sx={{mt: 1}} color="danger" variant="solid">{msg}</Alert>
}

export interface DebounceProps {
    handleDebounce: (value: string) => void;
    debounceTimeout: number;
}

export function DebouncedInput(props: InputProps & DebounceProps) {
    const {handleDebounce, debounceTimeout, ...rest} = props;

    const timerRef = React.useRef<number | null>(null);

    const handleChange = (event: React.ChangeEvent<HTMLInputElement>) => {
        if (timerRef.current) {
            clearTimeout(timerRef.current);
        }

        timerRef.current = window.setTimeout(() => {
            handleDebounce(event.target.value);
        }, debounceTimeout);
    };

    return <Input {...rest} onChange={handleChange}/>;
}

interface SearchFilterSnippet {
    snippet: string;
}

const blankSearchFilterSnippet = {snippet: ""};

export interface SearchFilterProps {
    onSet: (snippet: string) => void;
    onReset: () => void;
}

export function SearchFilter({onSet, onReset}: SearchFilterProps) {
    const {t} = useTranslation();
    const [filter, setFilter] = useState<SearchFilterSnippet>(blankSearchFilterSnippet);

    const onSubmit = useCallback(() => {
        onSet(filter.snippet);
    }, [filter.snippet, onSet]);

    return <Stack direction="row" sx={{pl: 2}} spacing={1}>
        <Input value={filter.snippet}
               onChange={(evt) => {
                   setFilter({snippet: evt.target.value});
               }}
               startDecorator={<FontAwesomeIcon icon={faSearch}/>}
        />
        <Box sx={{flex: 1}}/>
        <Button disabled={filter.snippet === ""}
                onClick={() => {
                    setFilter(blankSearchFilterSnippet);
                    onReset();
                }}
        >{t("resetSearchSnippet")}</Button>
        <Button disabled={!filter.snippet}
                onClick={onSubmit}
        >{t("filterSearchSnippet")}</Button>
    </Stack>
}
